import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-page">
      <Helmet>
        <title>Privacy Policy | Media Mars Limited</title>
        <meta name="description" content="Privacy Policy for Media Mars Limited. How we handle your data, cookies, and your rights under UK GDPR." />
        <link rel="canonical" href="https://www.media-mars.com/privacy" />
      </Helmet>

      <section className="privacy-hero">
        <div className="privacy-hero__inner">
          <h1>Privacy Policy</h1>
          <p className="privacy-updated">Last updated: 25 March 2026</p>
        </div>
      </section>

      <section className="privacy-content">
        <div className="privacy-content__inner">

          <div className="privacy-section">
            <h2>1. Who We Are</h2>
            <p>
              <strong>Media Mars Limited</strong> is a UK-registered IT consulting company based in
              Swindon, Wiltshire. We provide IT strategy, business automation, data analytics,
              system integration, IT coaching, hardware supply, and our document management
              platform <strong>SpocoDoc</strong>.
            </p>
            <ul className="privacy-details">
              <li><strong>Company:</strong> Media Mars Limited</li>
              <li><strong>Address:</strong> Rombourne Business Centres, Kingston House, Lydiard Fields, Swindon, Wiltshire SN5 8UB, UK</li>
              <li><strong>Email:</strong> <a href="mailto:info@media-mars.com">info@media-mars.com</a></li>
            </ul>
            <p>
              For any questions about this policy or how we handle your data, contact us using the
              details above. We are the data controller for the personal data described in this policy.
            </p>
          </div>

          <div className="privacy-section">
            <h2>2. What Data We Collect</h2>
            <p>We keep data collection to a minimum. Here is exactly what we collect and why:</p>

            <div className="privacy-card">
              <h3>When You Contact Us</h3>
              <p>
                If you email us or phone us, we receive whatever information you choose to share -
                typically your name, email address, phone number, and details of your enquiry.
                We use this solely to respond to you and discuss how we can help.
              </p>
            </div>

            <div className="privacy-card">
              <h3>When You Visit This Website</h3>
              <p>
                This website does not use any tracking cookies by default. If you click
                <strong> "Accept"</strong> on our cookie banner, we activate two analytics services:
              </p>
              <ul>
                <li><strong>Google Analytics 4 (GA4)</strong> - helps us understand which pages are visited and how visitors find us. IP addresses are anonymised.</li>
                <li><strong>Microsoft Clarity</strong> - helps us see how visitors interact with pages (clicks, scrolls) so we can improve the experience. No personal data is collected.</li>
              </ul>
              <p>
                If you click <strong>"Decline"</strong>, no analytics cookies are set and no
                tracking scripts are loaded. Your choice is saved in your browser's local storage
                so you are not asked again.
              </p>
            </div>

            <div className="privacy-card">
              <h3>Embedded Content</h3>
              <p>
                Our Contact page includes a Google Maps embed to show our office location.
                Google may set its own cookies when this map loads. See
                {' '}<a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a> for
                details.
              </p>
            </div>
          </div>

          <div className="privacy-section">
            <h2>3. How We Use Your Data</h2>
            <table className="privacy-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Purpose</th>
                  <th>Legal Basis</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Name, email, phone (from enquiry)</td>
                  <td>To respond to your enquiry and discuss services</td>
                  <td>Legitimate interest / contract performance</td>
                </tr>
                <tr>
                  <td>Analytics data (if you accept cookies)</td>
                  <td>To understand site usage and improve our services</td>
                  <td>Your consent</td>
                </tr>
                <tr>
                  <td>Cookie preference</td>
                  <td>To remember your cookie choice</td>
                  <td>Legitimate interest (necessary functionality)</td>
                </tr>
              </tbody>
            </table>
            <p>We do not use your data for automated decision-making or profiling.</p>
          </div>

          <div className="privacy-section">
            <h2>4. Cookies</h2>
            <p>Here are the cookies this website may use:</p>
            <table className="privacy-table">
              <thead>
                <tr>
                  <th>Cookie / Storage</th>
                  <th>Provider</th>
                  <th>Purpose</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>mediamars_cookie_consent</td>
                  <td>Media Mars (localStorage)</td>
                  <td>Stores your Accept/Decline choice</td>
                  <td>Persistent (until you clear browser data)</td>
                </tr>
                <tr>
                  <td>_ga, _ga_*</td>
                  <td>Google Analytics</td>
                  <td>Distinguish unique visitors, track sessions</td>
                  <td>Up to 2 years</td>
                </tr>
                <tr>
                  <td>_clck, _clsk, CLID</td>
                  <td>Microsoft Clarity</td>
                  <td>Session tracking and behaviour analytics</td>
                  <td>Up to 1 year</td>
                </tr>
              </tbody>
            </table>
            <p>
              Analytics cookies (Google Analytics and Microsoft Clarity) are only set if you
              click <strong>Accept</strong>. You can change your preference at any time by clearing
              your browser's local storage or site data for this website, which will reset the
              cookie banner.
            </p>
          </div>

          <div className="privacy-section">
            <h2>5. Who We Share Your Data With</h2>
            <p>We do not sell your data. We may share data with:</p>
            <ul>
              <li><strong>Google</strong> (Analytics) and <strong>Microsoft</strong> (Clarity) - only if you accept analytics cookies. Data is processed under their respective privacy policies.</li>
              <li><strong>Our hosting provider</strong> (Microsoft Azure) - standard server logs for security purposes.</li>
            </ul>
            <p>We do not transfer your personal data outside the UK or EEA unless adequate safeguards are in place (e.g. Standard Contractual Clauses).</p>
          </div>

          <div className="privacy-section">
            <h2>6. How Long We Keep Your Data</h2>
            <ul>
              <li><strong>Enquiry data</strong> (emails, phone records): retained for up to 2 years after your last interaction, or longer if we enter a business relationship.</li>
              <li><strong>Analytics data:</strong> retained as per Google and Microsoft retention settings (typically up to 14 months for GA4).</li>
              <li><strong>Cookie preference:</strong> stored in your browser until you clear it.</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h2>7. Your Rights</h2>
            <p>Under UK data protection law (UK GDPR and Data Protection Act 2018), you have the right to:</p>
            <div className="privacy-rights-grid">
              <div className="privacy-right">
                <strong>Access</strong>
                <p>Request a copy of the personal data we hold about you.</p>
              </div>
              <div className="privacy-right">
                <strong>Correction</strong>
                <p>Ask us to correct inaccurate or incomplete data.</p>
              </div>
              <div className="privacy-right">
                <strong>Erasure</strong>
                <p>Ask us to delete your personal data where there is no compelling reason to keep it.</p>
              </div>
              <div className="privacy-right">
                <strong>Restrict Processing</strong>
                <p>Ask us to limit how we use your data in certain circumstances.</p>
              </div>
              <div className="privacy-right">
                <strong>Object</strong>
                <p>Object to processing based on legitimate interest.</p>
              </div>
              <div className="privacy-right">
                <strong>Withdraw Consent</strong>
                <p>Where processing is based on consent (e.g. cookies), withdraw it at any time.</p>
              </div>
            </div>
            <p>
              To exercise any of these rights, email us at{' '}
              <a href="mailto:info@media-mars.com">info@media-mars.com</a>. We will respond within
              one month.
            </p>
          </div>

          <div className="privacy-section">
            <h2>8. Data Security</h2>
            <p>
              We take reasonable technical and organisational measures to protect your data. This
              website is served over HTTPS and hosted on Microsoft Azure with industry-standard
              security controls. However, no data transmission over the internet is 100% secure.
            </p>
          </div>

          <div className="privacy-section">
            <h2>9. Children's Privacy</h2>
            <p>
              Our services are aimed at businesses, not children. We do not knowingly collect data
              from anyone under 16. If you believe we have, please contact us and we will delete it
              promptly.
            </p>
          </div>

          <div className="privacy-section">
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this policy from time to time. The "last updated" date at the top of this
              page will reflect any changes. We encourage you to review this page periodically.
            </p>
          </div>

          <div className="privacy-section">
            <h2>11. Complaints</h2>
            <p>
              If you are unhappy with how we handle your data, please contact us first so we can
              try to resolve it. You also have the right to complain to the{' '}
              <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer">
                Information Commissioner's Office (ICO)
              </a>, the UK's data protection regulator.
            </p>
          </div>

          <div className="privacy-back">
            <Link to="/" className="privacy-back-link">&larr; Back to Home</Link>
          </div>

        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
